import { DOC_KEYS, type DocKey } from './keys';
import { isPlainObject } from './storage';
import type { AccountDocument, AccountState, ImportReceipt } from './types';

/**
 * What the app can use from each stored value: the same checks each hook's
 * own `read()` makes, so the import sends exactly what the user sees.
 *
 * Kept in step with:
 *   useCollections.ts / useHiddenEvents.ts  read()
 *   usePersonalEvents.ts                    read()
 *   useAttendance.ts                        read()
 *   useLegChoices.ts                        read()
 *   useHome.ts                              read()
 *   eventEdits.ts                           readJson() (no checks)
 */

export function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** usePersonalEvents.ts read(). The backend checks these as strictly (CONTRACT 4.4). */
export function isPersonalDraft(value: unknown): value is Record<string, unknown> & { id: string } {
  if (!isPlainObject(value)) return false;
  return (
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.dayKey === 'string' &&
    typeof value.startMinutes === 'number' &&
    typeof value.endMinutes === 'number'
  );
}

export function personalDrafts(value: unknown): (Record<string, unknown> & { id: string })[] {
  return Array.isArray(value) ? value.filter(isPersonalDraft) : [];
}

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** useAttendance.ts read(). */
export function attendance(value: unknown): Record<string, { start?: number; end?: number }> {
  const out: Record<string, { start?: number; end?: number }> = {};
  if (!value || typeof value !== 'object') return out;
  for (const [id, trim] of Object.entries(value as Record<string, unknown>)) {
    if (!trim || typeof trim !== 'object') continue;
    const { start, end } = trim as { start?: unknown; end?: unknown };
    const entry: { start?: number; end?: number } = {};
    if (finite(start)) entry.start = start;
    if (finite(end)) entry.end = end;
    if (Object.keys(entry).length > 0) out[id] = entry;
  }
  return out;
}

/** useLegChoices.ts read(). */
export function travelLegs(value: unknown): Record<string, { mode?: string; startMinutes?: number }> {
  const out: Record<string, { mode?: string; startMinutes?: number }> = {};
  if (!value || typeof value !== 'object') return out;
  for (const [id, choice] of Object.entries(value as Record<string, unknown>)) {
    if (!choice || typeof choice !== 'object') continue;
    const { mode, startMinutes } = choice as { mode?: unknown; startMinutes?: unknown };
    const entry: { mode?: string; startMinutes?: number } = {};
    if (typeof mode === 'string') entry.mode = mode;
    if (finite(startMinutes)) entry.startMinutes = startMinutes;
    if (Object.keys(entry).length > 0) out[id] = entry;
  }
  return out;
}

/** useHome.ts read(). */
export function home(value: unknown): { label: string; coords: [number, number] } | null {
  if (!isPlainObject(value)) return null;
  const { label, coords } = value;
  if (typeof label !== 'string' || !Array.isArray(coords) || coords.length !== 2) return null;
  if (!finite(coords[0]) || !finite(coords[1])) return null;
  return { label, coords: [coords[0], coords[1]] };
}

/** eventEdits.ts keeps whatever parses; so does the import. */
export function eventEdits(value: unknown): Record<string, unknown> {
  return isPlainObject(value) ? { ...value } : {};
}

// --- the server's answers -----------------------------------------------------

/** Throws when the answer isn't the shape §4.2 promises, so a bad answer is retried, never trusted. */
export function accountState(value: unknown): AccountState {
  if (!isPlainObject(value) || !isPlainObject(value.intents)) {
    throw new TypeError('GET /me/state: unexpected shape');
  }
  const { intents } = value;
  const documents = {} as Record<DocKey, AccountDocument | null>;
  const rawDocs = isPlainObject(value.documents) ? value.documents : {};
  for (const key of DOC_KEYS) {
    const doc = rawDocs[key];
    documents[key] =
      isPlainObject(doc) && typeof doc.version === 'number' ? { body: doc.body ?? null, version: doc.version } : null;
  }
  return {
    onboardingComplete: value.onboardingComplete === true,
    intents: {
      going: stringList(intents.going),
      saved: stringList(intents.saved),
      dismissed: stringList(intents.dismissed),
    },
    documents,
  };
}

export function importReceipt(value: unknown, importId: string): ImportReceipt {
  if (!isPlainObject(value)) throw new TypeError('POST /me/import: unexpected shape');
  const applied = isPlainObject(value.applied) ? value.applied : {};
  const count = (n: unknown) => (typeof n === 'number' ? n : 0);
  const unresolved = Array.isArray(value.unresolved)
    ? value.unresolved
        .filter((u): u is Record<string, unknown> => isPlainObject(u) && typeof u.id === 'string')
        .map((u) => ({ id: u.id as string, list: typeof u.list === 'string' ? u.list : '' }))
    : [];
  const versions: Partial<Record<DocKey, number>> = {};
  if (isPlainObject(value.documentVersions)) {
    for (const key of DOC_KEYS) {
      const v = value.documentVersions[key];
      if (typeof v === 'number') versions[key] = v;
    }
  }
  return {
    importId: typeof value.importId === 'string' ? value.importId : importId,
    replayed: value.replayed === true,
    applied: { going: count(applied.going), saved: count(applied.saved), dismissed: count(applied.dismissed) },
    unresolved,
    documentVersions: versions,
  };
}
